import { useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, Info, type LucideIcon } from 'lucide-react';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { Card } from '../ui/card';
import { cn } from '../../lib/utils';

/**
 * The building blocks of the metrics page: a metric card, the week-over-week change, trend charts
 * (Recharts) and per-person rows. Colours come from the console's CSS tokens, so a chart is light or
 * dark because the page is. The per-person rows stay plain markup: they are labelled lists more than
 * charts, and need to truncate names and expand to "Show all".
 */

/** "Sep 21" from a `YYYY-MM-DD`, read as a calendar date rather than an instant. */
export function shortDate(date: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
}

export function percent(share: number | null): string {
  return share === null ? '—' : `${Math.round(share * 100)}%`;
}

/**
 * Week-over-week change. Coloured by whether the move is good for *this* metric — more automations
 * running is good, more editing is not — so green always means better.
 */
export function Delta({
  value,
  previous,
  better,
  unit = '',
  scale = 1,
  digits = 0,
  since = 'last week',
}: {
  value: number | null;
  previous: number | null;
  better: 'up' | 'down';
  unit?: string;
  /** Multiplies the difference — 100 to show a share's change in points. */
  scale?: number;
  digits?: number;
  since?: string;
}) {
  if (value === null || previous === null) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-surface-muted text-ink-faint">No comparison yet</span>;
  }
  const diff = (value - previous) * scale;
  const shown = Number(Math.abs(diff).toFixed(digits));
  if (shown === 0) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-surface-muted text-ink-secondary">Same as {since}</span>;
  }
  const up = diff > 0;
  const good = up === (better === 'up');
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ring-1',
        good ? 'bg-success-bg text-success-text ring-success-ring' : 'bg-danger-bg text-danger-text ring-danger-ring',
      )}
    >
      <Icon size={11} />
      {shown}
      {unit} from {since}
    </span>
  );
}

/** What a trend tooltip shows: the period and its value. */
function TrendTooltip({
  active,
  payload,
  format,
  stacked,
}: {
  active?: boolean;
  payload?: Array<{ payload: TrendDatum }>;
  format: (value: number) => string;
  stacked: boolean;
}) {
  const datum = payload?.[0]?.payload;
  if (!active || !datum) return null;
  return (
    <div className="rounded-md border border-border bg-card px-2.5 py-1.5 text-[11px] shadow-sm">
      <p className="font-semibold text-ink">{datum.title}</p>
      {datum.value === null ? (
        <p className="text-ink-faint">Nothing to measure</p>
      ) : stacked ? (
        <p className="text-ink-secondary tabular-nums">
          {format(datum.value)} · {datum.lower} corrections, {datum.upper} updates
        </p>
      ) : (
        <p className="text-ink-secondary tabular-nums">{format(datum.value)}</p>
      )}
    </div>
  );
}

interface TrendDatum {
  key: string;
  title: string;
  value: number | null;
  lower: number | null;
  upper: number | null;
}

/**
 * The trend: one bar per period on one scale, the selected period in the accent and labelled. A
 * period with nothing to measure has no bar, and says so on hover, rather than reading as zero. With
 * `stacked`, each bar is two parts — the lower one darker.
 */
export function TrendBars({
  values,
  labels,
  selected,
  format,
  height = 112,
  stacked,
  titles,
}: {
  values: Array<number | null>;
  labels: string[];
  selected: number;
  format: (value: number) => string;
  height?: number;
  /** For a two-part bar: the lower part of each value. */
  stacked?: number[];
  /** Tooltip heading per bar; defaults to "Week of <date>". */
  titles?: string[];
}) {
  const data: TrendDatum[] = values.map((value, i) => ({
    key: labels[i]!,
    title: titles?.[i] ?? `Week of ${shortDate(labels[i]!)}`,
    value,
    lower: stacked ? (value === null ? null : stacked[i] ?? 0) : null,
    upper: stacked ? (value === null ? null : value - (stacked[i] ?? 0)) : null,
  }));
  // Recharts' label renderer must return an element, so an unlabelled bar gets an empty group.
  const label = (props: { x?: number | string; y?: number | string; width?: number | string; index?: number }) => {
    const value = values[selected];
    if (props.index !== selected || value === null || value === undefined) return <g />;
    return (
      <text
        x={Number(props.x) + Number(props.width) / 2}
        y={Number(props.y) - 5}
        textAnchor="middle"
        className="fill-ink"
        style={{ fontSize: 10, fontWeight: 600 }}
      >
        {format(value)}
      </text>
    );
  };
  const tick = { fontSize: 10, fill: 'var(--color-ink-faint)' };
  const edges = [labels[0], labels[labels.length - 1]];

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 16, right: 0, bottom: 0, left: 0 }} barCategoryGap="14%">
          <XAxis
            dataKey="key"
            tickLine={false}
            axisLine={false}
            tick={tick}
            ticks={edges as string[]}
            interval={0}
            tickFormatter={(key: string) => (titles ? (titles[labels.indexOf(key)] ?? key) : shortDate(key))}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-surface-subtle)' }}
            content={<TrendTooltip format={format} stacked={Boolean(stacked)} />}
          />
          {stacked ? (
            <>
              <Bar dataKey="lower" stackId="v" radius={[0, 0, 2, 2]} isAnimationActive={false}>
                {data.map((d, i) => (
                  <Cell key={d.key} fill={i === selected ? 'var(--color-accent)' : 'color-mix(in oklab, var(--color-accent) 45%, transparent)'} />
                ))}
              </Bar>
              <Bar dataKey="upper" stackId="v" radius={[2, 2, 0, 0]} isAnimationActive={false} label={label}>
                {data.map((d, i) => (
                  <Cell key={d.key} fill={i === selected ? 'color-mix(in oklab, var(--color-accent) 45%, transparent)' : 'color-mix(in oklab, var(--color-accent) 20%, transparent)'} />
                ))}
              </Bar>
            </>
          ) : (
            <Bar dataKey="value" radius={[2, 2, 0, 0]} isAnimationActive={false} label={label}>
              {data.map((d, i) => (
                <Cell key={d.key} fill={i === selected ? 'var(--color-accent)' : 'var(--color-surface-muted)'} />
              ))}
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase() || '?';
}

/** One row per person or automation: a name, a bar on the list's own scale, and the figure. */
export function BarList({
  rows,
  max,
  emptyText,
  limit = 8,
  avatars = true,
}: {
  rows: Array<{ key: string; name: string; value: number; lower?: number; display: ReactNode; extra?: ReactNode; note?: string }>;
  max?: number;
  emptyText: string;
  limit?: number;
  avatars?: boolean;
}) {
  const [all, setAll] = useState(false);
  if (rows.length === 0) return <p className="text-xs text-ink-faint py-2">{emptyText}</p>;
  const scale = Math.max(max ?? Math.max(...rows.map((r) => r.value)), 0.0001);
  const shown = all ? rows : rows.slice(0, limit);
  return (
    <div>
      <ul className="space-y-2">
        {shown.map((row) => (
          <li key={row.key} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-3 text-xs">
            <span className="flex items-center gap-2 min-w-0">
              {avatars && (
                <span className="w-5 h-5 rounded bg-surface-muted text-ink-secondary text-[9px] font-semibold flex items-center justify-center shrink-0">
                  {initials(row.name)}
                </span>
              )}
              <span className="truncate text-ink-secondary" title={row.note ?? row.name}>{row.name}</span>
            </span>
            <span className="h-2 rounded-sm bg-surface-muted overflow-hidden flex">
              {row.lower !== undefined && <span className="h-full bg-accent" style={{ width: `${(row.lower / scale) * 100}%` }} />}
              <span
                className={cn('h-full', row.lower !== undefined ? 'bg-accent/40' : 'bg-accent')}
                style={{ width: `${((row.value - (row.lower ?? 0)) / scale) * 100}%` }}
              />
            </span>
            <span className="flex items-center gap-2 justify-end tabular-nums text-ink font-medium">
              {row.display}
              {row.extra}
            </span>
          </li>
        ))}
      </ul>
      {rows.length > limit && (
        <button onClick={() => setAll((v) => !v)} className="mt-3 text-[11px] font-medium text-accent hover:text-accent-hover">
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

/** A metric: header, a left column with the figure and its trend, a right column with the breakdown. */
export function MetricCard({
  icon: Icon,
  title,
  tag,
  description,
  cadence,
  left,
  right,
  howMeasured,
  id,
}: {
  icon: LucideIcon;
  title: string;
  tag?: string;
  description: string;
  cadence: string;
  left: ReactNode;
  right: ReactNode;
  howMeasured: ReactNode;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card id={id} className="overflow-hidden">
      <div className="flex items-start gap-3 px-5 pt-4">
        <span className="w-8 h-8 rounded-md bg-accent/10 text-accent flex items-center justify-center shrink-0">
          <Icon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-ink">{title}</h3>
            {tag && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-accent/10 text-accent">{tag}</span>}
          </div>
          <p className="text-xs text-ink-muted mt-0.5">{description}</p>
        </div>
        <span className="shrink-0 px-2 py-0.5 rounded border border-border-subtle text-[11px] text-ink-muted">{cadence}</span>
      </div>
      <div className="grid gap-6 px-5 py-4 md:grid-cols-2">
        <div className="min-w-0">{left}</div>
        <div className="min-w-0">{right}</div>
      </div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-5 py-2.5 border-t border-border-subtle text-xs text-ink-muted hover:text-ink hover:bg-surface-subtle transition-colors"
      >
        <Info size={13} />
        How it&apos;s measured
        <ChevronDown size={13} className={cn('ml-auto transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="px-5 pb-4 pt-1 text-xs text-ink-muted leading-relaxed space-y-2 max-w-3xl">{howMeasured}</div>}
    </Card>
  );
}

/** The figure at the top of a card's left column. */
export function BigNumber({ value, unit }: { value: string; unit?: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-3xl font-bold text-ink tabular-nums leading-none">{value}</span>
      {unit && <span className="text-xs text-ink-muted">{unit}</span>}
    </div>
  );
}

/** A small headline tile: the number, its change, and a sparkline. */
export function HeadlineTile({
  icon: Icon,
  title,
  goal,
  value,
  caption,
  delta,
  spark,
  selected,
  href,
}: {
  icon: LucideIcon;
  title: string;
  goal: string;
  value: string;
  caption: string;
  delta: ReactNode;
  spark: Array<number | null>;
  selected: number;
  href: string;
}) {
  return (
    <a href={href} className="block">
      <Card className="px-4 py-3.5 hover:border-border-strong transition-colors h-full">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded bg-accent/10 text-accent flex items-center justify-center">
            <Icon size={13} />
          </span>
          <span className="text-xs font-semibold text-ink">{title}</span>
          <span className="ml-auto text-[10px] text-ink-faint">{goal}</span>
        </div>
        <div className="flex items-end justify-between gap-3 mt-3">
          <div className="min-w-0">
            <span className="block text-2xl font-bold text-ink tabular-nums leading-none">{value}</span>
            <span className="block text-[11px] text-ink-muted mt-1.5 truncate">{caption}</span>
          </div>
          <div className="w-24 h-9 shrink-0" aria-hidden>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spark.map((v, i) => ({ i, v }))} margin={{ top: 0, right: 0, bottom: 0, left: 0 }} barCategoryGap="18%">
                <Bar dataKey="v" radius={[1, 1, 0, 0]} isAnimationActive={false}>
                  {spark.map((_, i) => (
                    <Cell key={i} fill={i === selected ? 'var(--color-accent)' : 'var(--color-surface-muted)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="mt-2.5">{delta}</div>
      </Card>
    </a>
  );
}
